import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Divider,
  Flex,
  Input,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import React, { useMemo, useState } from 'react';
import {
  Control,
  get,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import styled from 'styled-components';

import { useDebounce } from 'src/app/hooks/useDebounce';

import {
  useMarketplaceOfferSelectOptions,
  useMarketplaceProperties,
} from '../hooks/useMarketplaceOptions';
import { AcquisitionType, MarketplaceOfferV2 } from '../types/types';
import { augmentOfferSelectOptions } from '../utils/marketplaceOfferOptions.utils';
import { FormItem } from './FormItem';
import { OfferOptionItem } from './OfferOptionItem';

const { Text } = Typography;

interface OfferBlockProps {
  namePrefix: string;
  control: Control<any>;
  typeLabel: string;
  audienceTypeId?: number;
  acquisitionId?: number;
  acquisitionType?: AcquisitionType;
  index?: number;
  onRemove?: () => void;
}

export const OfferBlock: React.FC<OfferBlockProps> = ({
  namePrefix,
  control,
  typeLabel,
  audienceTypeId,
  acquisitionId,
  acquisitionType,
  index,
  onRemove,
}) => {
  const [offerSearch, setOfferSearch] = useState('');
  const debouncedOfferSearch = useDebounce(offerSearch, 500) as string;

  const {
    selectOptions: offerSelectOptions,
    isLoading: isLoadingOffers,
    hasError: hasOffersError,
  } = useMarketplaceOfferSelectOptions({
    search: debouncedOfferSearch,
    typeId: audienceTypeId,
    acquisitionId,
  });

  const { field: promotionalOfferField } = useController({
    control,
    name: `${namePrefix}.promotionalOffer`,
  });

  const { field: baseOfferField } = useController({
    control,
    name: `${namePrefix}.basePaymentOfferId`,
  });

  const { field: tempOfferField } = useController({
    control,
    name: `${namePrefix}.tempPaymentOfferId`,
  });

  const baseOfferIdValue = useWatch({
    control,
    name: `${namePrefix}.basePaymentOfferId`,
  }) as string | undefined;

  const tempOfferIdValue = useWatch({
    control,
    name: `${namePrefix}.tempPaymentOfferId`,
  }) as string | undefined;

  const payOfferValue = useWatch({
    control,
    name: `${namePrefix}.payOffer`,
  }) as MarketplaceOfferV2 | undefined;

  const { properties } = useMarketplaceProperties();

  const augmentedOfferSelectOptions = useMemo(
    () =>
      augmentOfferSelectOptions(offerSelectOptions, payOfferValue, properties),
    [payOfferValue, offerSelectOptions, properties]
  );

  const offerNotFoundContent = hasOffersError
    ? 'Erro ao carregar ofertas. Tente novamente.'
    : 'Nenhuma oferta encontrada';

  const {
    formState: { errors },
  } = useFormContext();

  const isNumbered = index !== undefined;

  return (
    <BlockWrapper>
      {isNumbered && (
        <BlockHeader>
          <Space size={8} align='center'>
            <Avatar
              size={32}
              style={{
                backgroundColor: '#1677ff',
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {index + 1}
            </Avatar>
            <Text strong style={{ fontSize: 14 }}>
              {typeLabel}
            </Text>
          </Space>
          {onRemove && (
            <Button
              type='text'
              danger
              size='small'
              icon={<DeleteOutlined />}
              onClick={onRemove}
            />
          )}
        </BlockHeader>
      )}

      <BlockBody $hasHeader={isNumbered}>
        <FormItem
          label={
            <Space size={8}>
              Selecionar Oferta
              <Tooltip
                title='Selecione a oferta cadastrada no Pay. 
                       Use o ID, nome ou valor para encontrar.'
              >
                <QuestionCircleOutlined style={{ color: '#595959' }} />
              </Tooltip>
            </Space>
          }
          required
          control={control}
          name={`${namePrefix}.basePaymentOfferId`}
          renderItem={() => (
            <Flex vertical gap={6}>
              <Select
                style={{ width: '100%' }}
                showSearch
                loading={isLoadingOffers}
                allowClear
                placeholder='Pesquise por ID ou nome...'
                filterOption={false}
                onSearch={setOfferSearch}
                options={augmentedOfferSelectOptions}
                optionRender={option => (
                  <OfferOptionItem data={(option.data as any).offer} />
                )}
                labelRender={() => {
                  if (!baseOfferIdValue) return null;
                  const opt = augmentedOfferSelectOptions.find(
                    o => o.value === baseOfferIdValue
                  );
                  if (!opt) return <span>{baseOfferIdValue}</span>;
                  return <OfferOptionItem data={opt.offer} compact />;
                }}
                value={baseOfferIdValue || undefined}
                onChange={baseOfferField.onChange}
                onClear={() => baseOfferField.onChange(undefined)}
                onBlur={baseOfferField.onBlur}
                notFoundContent={offerNotFoundContent}
                status={
                  get(errors, `${namePrefix}.basePaymentOfferId`)
                    ? 'error'
                    : undefined
                }
              />
              {hasOffersError && (
                <Text type='danger' style={{ fontSize: 12 }}>
                  Não foi possível carregar as ofertas agora.
                </Text>
              )}
            </Flex>
          )}
        />

        <Divider size='small' />

        <Flex vertical gap={8} style={{ width: '100%' }}>
          <PromoOfferHeader>
            <Text strong>Oferta Promocional</Text>
            <Switch
              size='small'
              checked={promotionalOfferField.value}
              onChange={(checked: boolean) => {
                if (!checked) tempOfferField.onChange('');
                promotionalOfferField.onChange(checked);
              }}
            />
          </PromoOfferHeader>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Substitua a oferta principal por tempo limitado (ex: Black Friday,
            promoções)
          </Text>

          {promotionalOfferField.value && (
            <PromoOfferCard>
              <Flex vertical gap={12}>
                <FormItem
                  label={
                    <Space size={8}>
                      Selecione a Oferta Temporária
                      <Tooltip title='Oferta que substituirá a principal durante o período promocional'>
                        <QuestionCircleOutlined style={{ color: '#595959' }} />
                      </Tooltip>
                    </Space>
                  }
                  required
                  control={control}
                  name={`${namePrefix}.tempPaymentOfferId`}
                  renderItem={() => (
                    <Flex vertical gap={6}>
                      <Select
                        style={{ width: '100%' }}
                        showSearch
                        loading={isLoadingOffers}
                        allowClear
                        placeholder='Pesquise por ID ou nome...'
                        filterOption={false}
                        onSearch={setOfferSearch}
                        options={augmentedOfferSelectOptions}
                        optionRender={option => (
                          <OfferOptionItem data={(option.data as any).offer} />
                        )}
                        labelRender={() => {
                          if (!tempOfferIdValue) return null;
                          const opt = augmentedOfferSelectOptions.find(
                            o => o.value === tempOfferIdValue
                          );
                          if (!opt) return <span>{tempOfferIdValue}</span>;
                          return <OfferOptionItem data={opt.offer} compact />;
                        }}
                        value={tempOfferIdValue || undefined}
                        onChange={tempOfferField.onChange}
                        onClear={() => tempOfferField.onChange(undefined)}
                        onBlur={tempOfferField.onBlur}
                        notFoundContent={offerNotFoundContent}
                        status={
                          get(errors, `${namePrefix}.tempPaymentOfferId`)
                            ? 'error'
                            : undefined
                        }
                      />
                      {hasOffersError && (
                        <Text type='danger' style={{ fontSize: 12 }}>
                          Não foi possível carregar as ofertas agora.
                        </Text>
                      )}
                    </Flex>
                  )}
                />
              </Flex>
            </PromoOfferCard>
          )}
        </Flex>

        {acquisitionType === AcquisitionType.Subscription && (
          <>
            <Divider size='small' />

            <FormItem
              label='Tag'
              control={control}
              name={`${namePrefix}.tag`}
              renderItem={({ field }) => (
                <Input
                  {...field}
                  placeholder='Ex: 30% de desconto, Black Friday, Economize...'
                  status={
                    get(errors, `${namePrefix}.tag`) ? 'error' : undefined
                  }
                />
              )}
            />
          </>
        )}
      </BlockBody>
    </BlockWrapper>
  );
};

const BlockWrapper = styled.div`
  border: 1px solid #303030;
  border-radius: 8px;
  overflow: hidden;
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #303030;
`;

const BlockBody = styled.div<{ $hasHeader?: boolean }>`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PromoOfferHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PromoOfferCard = styled.div`
  background-color: #000;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
